import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  GetAlertStockSettingAPI,
  UpdateAlertStockSettingAPI,
} from "../../../api/settings/AlertStockSettingController";

const AlertStockSettingsPage = () => {
  const theme = useTheme();
  const [alertStockThreshold, setAlertStockThreshold] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const loadSetting = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const data = await GetAlertStockSettingAPI();
        if (data?.baseResponseModel?.respCode === 200) {
          setAlertStockThreshold(data?.setting?.alertStockThreshold ?? 5);
        } else {
          setLoadError(
            data?.baseResponseModel?.respMessage ||
              "Failed to load alert stock settings."
          );
        }
      } catch (error) {
        setLoadError("Failed to load alert stock settings.");
      } finally {
        setIsLoading(false);
      }
    };

    loadSetting();
  }, []);

  const handleSave = async () => {
    if (alertStockThreshold === "" || Number.isNaN(Number(alertStockThreshold))) {
      toast.error("Please enter a valid quantity.");
      return;
    }

    const parsedValue = Number(alertStockThreshold);
    if (parsedValue < 0) {
      toast.error("Alert quantity cannot be negative.");
      return;
    }

    try {
      setIsSaving(true);
      const response = await UpdateAlertStockSettingAPI(parsedValue);
      if (response?.baseResponseModel?.respCode === 200) {
        setAlertStockThreshold(response?.setting?.alertStockThreshold ?? parsedValue);
        toast.success("Alert stock quantity updated.");
      } else {
        toast.error(
          response?.baseResponseModel?.respMessage ||
            "Failed to update alert stock quantity."
        );
      }
    } catch (error) {
      toast.error("Failed to update alert stock quantity.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Stock Alert Settings
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Set the quantity that triggers low stock alerts on the dashboard.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.action.main}`,
          background: "#FFFFFF",
          maxWidth: 520,
        }}
      >
        <Stack spacing={2.5}>
          {isLoading ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading settings...
            </Typography>
          ) : loadError ? (
            <Typography variant="body2" sx={{ color: "error.main" }}>
              {loadError}
            </Typography>
          ) : (
            <>
              <TextField
                label="Alert stock quantity"
                type="number"
                value={alertStockThreshold}
                onChange={(event) => setAlertStockThreshold(event.target.value)}
                inputProps={{ min: 0, max: 100000, step: 1 }}
                helperText="Products at or below this quantity will show as low stock."
              />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
                sx={{ alignSelf: "flex-start" }}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default AlertStockSettingsPage;
